import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { calculateOrderItemTotal, formatCurrency, getItemDiscount, getItemUnitPrice, normalizeMoney } from '../services/dataService';
import { Button } from '../components/Button';
import { ProductCustomizationModal } from '../components/ProductCustomizationModal';
import { SpotlightTour, SpotlightStep } from '../components/SpotlightTour';
import { api } from '../services/api';
import { Payment, Product, Table, POSMode } from 'types';
import { Trash2, UtensilsCrossed, Settings, Printer, Check, Wallet, QrCode, CreditCard, RefreshCw as RefreshIcon, ArrowLeft, ChevronLeft, ChevronRight, CheckCircle, Ban, Clock, ArrowRightLeft, Pencil, ShieldCheck, Calculator, ClipboardCheck, Banknote, FileText, AlertTriangle, Package, ScanBarcode, ShoppingCart, Truck, Warehouse, Scale as ScaleIcon, CircleHelp } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { advanceFirstAccessTour, beginFirstAccessTour, getActiveTourArea, getPendingTourRoute, getTourAreas, stopFirstAccessTour } from '../utils/firstAccessTour';

const COUNTER_TABLE_ID = 0;

const posTourSteps: SpotlightStep[] = [
  { target: 'pos-tables', title: 'Comece por uma mesa ou balcão', description: 'Escolha onde o pedido será lançado. A cor indica se a mesa está livre, ocupada ou aguardando pagamento.' },
  { target: 'pos-order', title: 'Acompanhe a comanda aqui', description: 'Os itens adicionados, quantidades e subtotal aparecem neste espaço. Durante o guia, nenhuma venda é gravada.' },
  { target: 'pos-search', title: 'Encontre um produto rapidamente', description: 'Pesquise pelo nome do item. Nos modos de venda direta, você também pode usar código ou leitor de barras.' },
  { target: 'pos-categories', title: 'Filtre por categoria', description: 'Toque em uma categoria para mostrar somente os produtos dela.' },
  { target: 'pos-products', title: 'Adicione o produto ao pedido', description: 'Normalmente, basta tocar no produto para lançá-lo na comanda selecionada.' },
  { target: 'pos-actions', title: 'Gerencie o pedido', description: 'Use estas ações para imprimir, concluir ou ajustar o pedido. Algumas operações exigem autorização do dono ou gerente.' },
  { target: 'pos-charge', title: 'Hora de cobrar', description: 'Depois de lançar os produtos, abra Fechar Conta para conferir o total e registrar os pagamentos.' },
  { target: 'pos-payment', title: 'Escolha a forma de pagamento', description: 'Dinheiro, Pix, crédito e débito podem ser registrados aqui. Também é possível dividir a conta ou receber um adiantamento.', detail: 'Esta tela é uma prévia do tutorial: nenhum pagamento será lançado.' }
];

type TradePOSMode = Exclude<POSMode, 'STANDARD'>;

const isSellableProduct = (product: Product) => (
  product.internalUseOnly !== true &&
  product.active !== false &&
  product.allowSale !== false
);

const getSafePOSMode = (mode?: string): POSMode => {
  if (mode === 'DISTRIBUTOR' || mode === 'MARKET') return mode;
  return 'STANDARD';
};

const getDesktopPosScale = () => {
  if (typeof window === 'undefined') return 1;
  const width = window.innerWidth;
  const height = window.innerHeight;
  if (width < 1024) return 1;

  const widthScale = width >= 1280 ? 1 : width / 1280;
  const heightScale = height >= 760 ? 1 : height / 760;
  return Math.min(1, Math.max(0.78, Math.min(widthScale, heightScale)));
};

const TRADE_POS_COPY: Record<TradePOSMode, {
  eyebrow: string;
  title: string;
  subtitle: string;
  saleTitle: string;
  saleStatus: string;
  empty: string;
  searchPlaceholder: string;
  closeLabel: string;
  guidance: Array<{ title: string; text: string; icon: React.ElementType }>;
}> = {
  DISTRIBUTOR: {
    eyebrow: 'PDV Distribuidora',
    title: 'Balcão de distribuidora',
    subtitle: 'Venda direta para caixas, fardos, retirada e entrega sem fluxo de mesas.',
    saleTitle: 'Balcão 01',
    saleStatus: 'Caixa de distribuidora',
    empty: 'Adicione itens para iniciar o pedido.',
    searchPlaceholder: 'Buscar item, caixa, fardo, bebida ou cliente...',
    closeLabel: 'Fechar Pedido',
    guidance: [
      { title: 'Venda no balcão', text: 'Selecione produtos à direita e acompanhe o pedido no centro.', icon: Warehouse },
      { title: 'Caixas e fardos', text: 'Produtos compostos continuam baixando o estoque correto.', icon: Package },
      { title: 'Pronto para expansão', text: 'Estrutura preparada para novos balcões/caixas no futuro.', icon: Truck },
    ],
  },
  MARKET: {
    eyebrow: 'PDV Mercado',
    title: 'Frente de caixa',
    subtitle: 'Checkout simples para venda rápida, produtos pesáveis e fechamento limpo.',
    saleTitle: 'Caixa 01',
    saleStatus: 'Frente de caixa',
    empty: 'Passe, busque ou selecione produtos para iniciar a venda.',
    searchPlaceholder: 'Buscar produto, código, SKU ou item pesado...',
    closeLabel: 'Finalizar Venda',
    guidance: [
      { title: 'Produtos à direita', text: 'Busque por nome, categoria ou código e toque para adicionar.', icon: ScanBarcode },
      { title: 'Peso e unidade', text: 'Itens por kg usam a balança quando o recurso estiver ativo.', icon: ScaleIcon },
      { title: 'Caixas futuros', text: 'Modelo pronto para múltiplos caixas quando decidirmos ativar.', icon: ShoppingCart },
    ],
  },
};

const OpenCashierScreen = () => {
  const { openCashier, currentCompany, user } = useStore();
  const [openingMode, setOpeningMode] = useState<'SIMPLE' | 'DETAILED'>('SIMPLE');
  const [simpleAmount, setSimpleAmount] = useState('');
  const [extraAmount, setExtraAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [denominations, setDenominations] = useState<Record<string, string>>({});
  const [checklist, setChecklist] = useState({ drawer: false, printer: false, cardMachine: false, manager: false });
  const [loading, setLoading] = useState(false);

  const denominationValues = useMemo(() => [
    { label: 'R$ 200', value: 200 }, { label: 'R$ 100', value: 100 }, { label: 'R$ 50', value: 50 },
    { label: 'R$ 20', value: 20 }, { label: 'R$ 10', value: 10 }, { label: 'R$ 5', value: 5 },
    { label: 'R$ 2', value: 2 }, { label: 'R$ 1', value: 1 }, { label: 'R$ 0,50', value: 0.5 },
    { label: 'R$ 0,25', value: 0.25 }, { label: 'R$ 0,10', value: 0.1 }, { label: 'R$ 0,05', value: 0.05 }
  ], []);

  const countedCash = useMemo(() => denominationValues.reduce((total, item) => {
    const quantity = Number(denominations[String(item.value)] || 0);
    return total + (quantity * item.value);
  }, 0), [denominationValues, denominations]);

  const simpleFloat = Number(simpleAmount.replace(',', '.') || 0);
  const extraFloat = Number(extraAmount.replace(',', '.') || 0);
  const openingTotal = openingMode === 'DETAILED'
    ? countedCash + (Number.isFinite(extraFloat) ? extraFloat : 0)
    : (Number.isFinite(simpleFloat) ? simpleFloat : 0);
  const checklistDone = Object.values(checklist).filter(Boolean).length;
  const isChecklistComplete = checklistDone === Object.values(checklist).length;
  const checklistLabels: Record<keyof typeof checklist, string> = {
    drawer: 'Gaveta conferida e com troco correto',
    printer: 'Impressora ligada e com papel',
    cardMachine: 'Maquininha/Pix funcionando',
    manager: 'Responsável ciente da abertura'
  };

  const handleOpen = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!Number.isFinite(openingTotal) || openingTotal < 0) {
      alert('Por favor, confira os valores informados para o fundo de troco.');
      return;
    }
    if (!isChecklistComplete && !window.confirm('O checklist ainda não foi concluído. Deseja abrir o caixa mesmo assim?')) return;
    if (openingTotal >= 5000 && !window.confirm(`Esse valor inicial é alto (${formatCurrency(openingTotal)}). Confirma que está correto?`)) return;

    try {
      setLoading(true);
      const completedChecklist = Object.entries(checklist)
        .filter(([, done]) => done)
        .map(([key]) => checklistLabels[key as keyof typeof checklist]);
      const success = await openCashier(openingTotal, {
        mode: openingMode,
        simpleAmount: Number.isFinite(simpleFloat) ? simpleFloat : 0,
        countedCash,
        extraFloat: Number.isFinite(extraFloat) ? extraFloat : 0,
        denominations: openingMode === 'DETAILED' ? denominations : {},
        checklist: completedChecklist,
        notes: notes.trim() || null
      });
      if (!success) {
        alert('Não foi possível abrir o caixa. Tente novamente.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Erro ao abrir caixa:', error);
      alert('Não foi possível abrir o caixa. Tente novamente.');
      setLoading(false);
    }  };

  return (
    <div className="flex-1 bg-slate-950 text-white overflow-y-auto">
      <div className="min-h-full p-4 md:p-8 flex items-center justify-center">
        <form onSubmit={handleOpen} className="w-full max-w-6xl">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-5 items-stretch">
            <section className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950 border border-white/10 shadow-2xl p-6 md:p-8">
              <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-emerald-400/20 blur-3xl"></div>
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-300/10 px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-100"><ShieldCheck className="w-4 h-4" /> Abertura segura</div>
                <h1 className="mt-5 text-4xl md:text-5xl font-black tracking-tight">Caixa fechado</h1>
                <p className="mt-3 text-slate-300 max-w-xl text-sm md:text-base">Antes de vender, confira o dinheiro da gaveta, registre o fundo de troco e valide os equipamentos do balcão.</p>

                <div className="mt-8 grid sm:grid-cols-3 gap-3">
                  <div className="rounded-3xl border border-white/10 bg-white/10 p-4"><p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Empresa</p><p className="mt-1 font-black text-lg truncate">{currentCompany?.name || 'FlowTab'}</p></div>
                  <div className="rounded-3xl border border-white/10 bg-white/10 p-4"><p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Operador</p><p className="mt-1 font-black text-lg truncate">{user?.name || 'Usuário'}</p></div>
                  <div className="rounded-3xl border border-white/10 bg-white/10 p-4"><p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Checklist</p><p className="mt-1 font-black text-lg">{checklistDone}/4 pronto</p></div>
                </div>

                <div className="mt-6 rounded-[2rem] bg-white text-slate-950 p-5 shadow-xl">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3"><Calculator className="w-10 h-10 rounded-2xl bg-slate-950 text-white p-2" /><div><h2 className="font-black text-xl">Valor de abertura</h2><p className="text-xs font-bold text-slate-400">Use valor total ou conferência por cédulas.</p></div></div>
                    <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-2xl p-1">
                      <button type="button" onClick={() => setOpeningMode('SIMPLE')} className={`px-3 py-2 rounded-xl text-xs font-black ${openingMode === 'SIMPLE' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}>Simples</button>
                      <button type="button" onClick={() => setOpeningMode('DETAILED')} className={`px-3 py-2 rounded-xl text-xs font-black ${openingMode === 'DETAILED' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}>Detalhado</button>
                    </div>
                  </div>
                  {openingMode === 'SIMPLE' ? (
                    <label className="block rounded-3xl border border-slate-200 bg-slate-50 p-5">
                      <span className="block text-[10px] font-black uppercase tracking-widest text-slate-400">Fundo de troco total</span>
                      <input type="text" value={simpleAmount} onChange={event => setSimpleAmount(event.target.value)} placeholder="Ex: 150,00" className="mt-3 w-full bg-white rounded-2xl p-5 text-center text-4xl font-black outline-none border border-slate-200 focus:border-brand-500" autoFocus />
                      <span className="mt-3 block text-xs font-bold text-slate-400">Modo rápido: informe o total que está entrando no caixa.</span>
                    </label>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {denominationValues.map(item => (
                        <label key={item.value} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                          <span className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{item.label}</span>
                          <input type="number" min="0" step="1" value={denominations[String(item.value)] || ''} onChange={event => setDenominations(prev => ({ ...prev, [String(item.value)]: event.target.value }))} className="mt-1 w-full bg-white rounded-xl p-2 text-center font-black outline-none border border-slate-200 focus:border-brand-500" placeholder="0" />
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="rounded-[2rem] bg-white text-slate-950 border border-slate-200 shadow-2xl p-5 md:p-6 space-y-4">
              <div className="flex items-center gap-3"><Banknote className="w-11 h-11 rounded-2xl bg-emerald-50 text-emerald-600 p-2" /><div><h2 className="text-2xl font-black">Resumo da abertura</h2><p className="text-xs font-bold text-slate-400">Este será o valor inicial do caixa.</p></div></div>
              {openingMode === 'DETAILED' ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-3xl bg-slate-50 border border-slate-200 p-4"><p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Gaveta contada</p><p className="mt-1 text-2xl font-black">{formatCurrency(countedCash)}</p></div>
                    <div className="rounded-3xl bg-slate-50 border border-slate-200 p-4"><p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Suprimento extra</p><p className="mt-1 text-2xl font-black">{formatCurrency(Number.isFinite(extraFloat) ? extraFloat : 0)}</p></div>
                  </div>
                  <label className="block rounded-3xl border border-slate-200 bg-slate-50 p-4"><span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Valor extra/manual</span><input type="text" value={extraAmount} onChange={event => setExtraAmount(event.target.value)} placeholder="Ex: 50,00" className="mt-2 w-full bg-white border border-slate-200 rounded-2xl p-4 text-2xl font-black text-center outline-none focus:border-brand-500" /><span className="mt-2 block text-[11px] font-bold text-slate-400">Use se tiver reforço de troco fora da contagem por cédulas.</span></label>
                </>
              ) : (
                <div className="rounded-3xl bg-slate-50 border border-slate-200 p-4">
                  <p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Modo atual</p>
                  <p className="mt-1 text-2xl font-black">Valor total informado</p>
                  <p className="mt-1 text-xs font-bold text-slate-400">Se quiser conferir por cédulas e moedas, mude para Detalhado.</p>
                </div>
              )}
              <div className="rounded-3xl bg-slate-950 text-white p-5"><p className="text-[10px] uppercase font-black tracking-widest text-slate-400">Total de abertura</p><p className="mt-1 text-4xl font-black">{formatCurrency(openingTotal)}</p></div>
              <div className="rounded-3xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-3"><ClipboardCheck className="w-5 h-5 text-brand-600" /><h3 className="font-black">Checklist operacional</h3></div>
                <div className="grid gap-2">
                  {Object.entries(checklistLabels).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-3 rounded-2xl bg-slate-50 border border-slate-200 p-3 cursor-pointer"><input type="checkbox" checked={checklist[key as keyof typeof checklist]} onChange={event => setChecklist(prev => ({ ...prev, [key]: event.target.checked }))} className="w-5 h-5 accent-brand-600" /><span className="text-sm font-bold text-slate-700">{label}</span></label>
                  ))}
                </div>
              </div>
              <label className="block rounded-3xl border border-slate-200 bg-slate-50 p-4"><span className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400"><FileText className="w-4 h-4" /> Observação da abertura</span><textarea value={notes} onChange={event => setNotes(event.target.value)} placeholder="Ex: abertura feita pelo gerente, troco reforçado, impressora revisada..." className="mt-2 w-full min-h-20 resize-none bg-white border border-slate-200 rounded-2xl p-3 text-sm font-bold outline-none focus:border-brand-500" /></label>
              {!isChecklistComplete && <div className="flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-100 p-3 text-amber-800"><AlertTriangle className="w-5 h-5 flex-shrink-0" /><p className="text-xs font-bold">Você ainda pode abrir o caixa, mas o sistema vai pedir confirmação se o checklist não estiver completo.</p></div>}
              <Button type="submit" size="xl" className="w-full h-16 rounded-2xl shadow-xl" disabled={loading}>{loading ? <RefreshIcon className="animate-spin" /> : 'Abrir Caixa e Liberar PDV'}</Button>
            </section>
          </div>
        </form>
      </div>
    </div>
  );
};

export const POS = () => {
  const { 
    tables, products, categories, addOrderToTable, updateTableCount, removeItemFromTable,
    requestBill, revertToOccupied, closeTable, toggleServiceFee, updateOrderItemQuantity, updateOrderItemDiscount, setTableDiscount,
    verifyManagerAuth, logManagerAction, currentCompany, addPartialPayments, resetTablePayments, cancelTable,
    transferTable, updateTableName, cashierShift, user
  } = useStore();
  
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTableId, setActiveTableId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Todos');
  const [showConfig, setShowConfig] = useState(false);
  const [tempTableCount, setTempTableCount] = useState(12);
  const [tempPayments, setTempPayments] = useState<Payment[]>([]);
  const [paymentMode, setPaymentMode] = useState<'advance' | 'close' | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<Payment['method'] | null>(null);
  const [paymentValue, setPaymentValue] = useState('');
  const [generalDiscountInput, setGeneralDiscountInput] = useState('');
  const [splitPeople, setSplitPeople] = useState(1);
  const [discountItemIndex, setDiscountItemIndex] = useState(0);
  const [isFinishing, setIsFinishing] = useState(false);
  const [authAction, setAuthAction] = useState<{ action: 'decrease' | 'delete' | 'reset_payment' | 'cancel_table' | 'item_discount' | 'general_discount', itemId?: string, discount?: number } | null>(null);
  const [authPassword, setAuthPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [decreaseAmount, setDecreaseAmount] = useState(1);
  const [receiptData, setReceiptData] = useState<any>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferTargetId, setTransferTargetId] = useState('');
  const [tableNameModal, setTableNameModal] = useState<{ tableId: number; value: string } | null>(null);
  const [pendingMergeTarget, setPendingMergeTarget] = useState<number | null>(null);
  const [scaleProduct, setScaleProduct] = useState<Product | null>(null);
  const [customizingProduct, setCustomizingProduct] = useState<Product | null>(null);
  const [scaleWeight, setScaleWeight] = useState('');
  const [tourOpen, setTourOpen] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourDismissed, setTourDismissed] = useState(false);
  const originalTourTableRef = useRef<number | null>(null);
  const activeTableIdRef = useRef<number | null>(null);
  activeTableIdRef.current = activeTableId;
  
  const categoriesContainerRef = useRef<HTMLDivElement>(null);
  const scannerBufferRef = useRef('');
  const scannerResetTimerRef = useRef<number | null>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [desktopScale, setDesktopScale] = useState(getDesktopPosScale);
  const posMode = getSafePOSMode(currentCompany?.posMode);
  const isTradeMode = posMode !== 'STANDARD';
  const tradeCopy = isTradeMode ? TRADE_POS_COPY[posMode] : null;
  const tourStorageKey = `flowtab-pos-tour-v1:${user?.id}`;
  const activeTourArea = user ? getActiveTourArea(user.id) : null;
  const pendingProtectedArea = user && activeTourArea && getTourAreas(user).includes(activeTourArea) ? activeTourArea : null;
  const pendingProtectedGuide = pendingProtectedArea === 'stock' ? { label: 'Estoque', route: '/stock' }
    : pendingProtectedArea === 'reports' ? { label: 'Relatórios', route: '/reports' }
    : pendingProtectedArea === 'settings' ? { label: 'Configurações', route: '/settings' } : null;
  const showTourExample = tourOpen && !products.some(isSellableProduct);
  const currentTourSteps = useMemo(() => posTourSteps.map(step => {
    if (!showTourExample) return step;
    if (step.target === 'pos-categories') return { ...step, detail: 'A categoria “Exemplo” aparece somente durante o guia.' };
    if (step.target === 'pos-products') return { ...step, detail: 'Este produto é visual e não será cadastrado nem lançado no estoque.' };
    return step;
  }), [showTourExample]);

  useEffect(() => {
    if (!user?.id || !currentCompany?.id || tourOpen) return;
    const pendingRoute = getPendingTourRoute(user);
    if (pendingRoute && pendingRoute !== '/pos') {
      navigate(pendingRoute, { replace: true });
      return;
    }
    if (getActiveTourArea(user.id) === 'pos') {
      originalTourTableRef.current = activeTableIdRef.current;
      setSearchTerm('');
      setCategoryFilter('Todos');
      setTourStep(0);
      setTourOpen(true);
      return;
    }
    let active = true;
    const checkTour = async () => {
      const status = await api.auth.getTutorialStatus('pos');
      if (!active) return;
      const seenLocally = window.localStorage.getItem(tourStorageKey) === 'seen';
      if (seenLocally && status && !status.completed) void api.auth.completeTutorial('pos');
      if (seenLocally || status?.completed) {
        window.localStorage.setItem(tourStorageKey, 'seen');
        return;
      }
      beginFirstAccessTour(user);
      originalTourTableRef.current = activeTableIdRef.current;
      setSearchTerm('');
      setCategoryFilter('Todos');
      setTourStep(0);
      setTourOpen(true);
    };
    void checkTour();
    return () => { active = false; };
  }, [user, currentCompany?.id, tourOpen, tourStorageKey, navigate]);

  const startTour = () => {
    originalTourTableRef.current = activeTableId;
    setSearchTerm('');
    setCategoryFilter('Todos');
    setTourStep(0);
    setTourOpen(true);
  };

  const changeTourStep = (index: number) => {
    if (index > 0 && (activeTableId === null || tables.find(table => table.id === activeTableId)?.status === 'PAYING')) {
      const availableTable = tables.find(table => table.status !== 'PAYING');
      if (availableTable) setActiveTableId(availableTable.id);
    } else if (index === 0 && originalTourTableRef.current === null && !isTradeMode) {
      setActiveTableId(null);
    }
    setTourStep(index);
  };

  const finishTour = () => {
    window.localStorage.setItem(tourStorageKey, 'seen');
    void api.auth.completeTutorial('pos');
    const nextRoute = user ? advanceFirstAccessTour(user, 'pos') : null;
    setTourOpen(false);
    setTourStep(0);
    setActiveTableId(originalTourTableRef.current);
    if (nextRoute) navigate(nextRoute, { replace: true });
  };

  useEffect(() => {
    const updateScale = () => setDesktopScale(getDesktopPosScale());
    updateScale();
    window.addEventListener('resize', updateScale);
    window.visualViewport?.addEventListener('resize', updateScale);
    return () => {
      window.removeEventListener('resize', updateScale);
      window.visualViewport?.removeEventListener('resize', updateScale);
    };
  }, []);

  useEffect(() => {
    // Define o estado inicial da mesa ativa após os dados da empresa serem carregados
    if (currentCompany && activeTableId === null && !location.state?.tableId) {
      if (currentCompany.useCounterMode || getSafePOSMode(currentCompany.posMode) !== 'STANDARD') {
        setActiveTableId(COUNTER_TABLE_ID);
      }
    }
  }, [currentCompany, activeTableId, location.state]);

  const activeTable = useMemo(() => tables.find(t => t.id === activeTableId), [tables, activeTableId]);
  const isPaying = activeTable?.status === 'PAYING';

  const grossSubTotal = useMemo(() => activeTable ? activeTable.items.reduce((acc, item) => acc + Number(item.price || 0) * Number(item.quantity || 0), 0) : 0, [activeTable]);
  const itemDiscountTotal = useMemo(() => activeTable ? activeTable.items.reduce((acc, item) => acc + getItemDiscount(item) * Number(item.quantity || 0), 0) : 0, [activeTable]);
  const subTotal = useMemo(() => activeTable ? activeTable.total : 0, [activeTable]);
  const generalDiscount = useMemo(() => activeTable ? Math.min(normalizeMoney(activeTable.discount), subTotal) : 0, [activeTable, subTotal]);
  const discountedSubTotal = useMemo(() => Math.max(0, subTotal - generalDiscount), [subTotal, generalDiscount]);
  const isServiceFeeEnabled = currentCompany?.useServiceFee === true;
  const serviceFeeValue = useMemo(() => isServiceFeeEnabled && activeTable?.serviceFee ? discountedSubTotal * 0.10 : 0, [activeTable, discountedSubTotal, isServiceFeeEnabled]);
  const finalTotal = useMemo(() => discountedSubTotal + serviceFeeValue, [discountedSubTotal, serviceFeeValue]);
  
  const previouslyPaid = useMemo(() => activeTable?.payments?.reduce((acc, p) => acc + p.amount, 0) || 0, [activeTable]);
  const newlyPaid = useMemo(() => tempPayments.reduce((acc, p) => acc + p.amount, 0), [tempPayments]);
  const totalPaid = previouslyPaid + newlyPaid;
  
  const amountDue = useMemo(() => finalTotal - totalPaid, [finalTotal, totalPaid]);
  const discountItem = activeTable?.items[Math.min(discountItemIndex, Math.max(0, (activeTable?.items.length || 1) - 1))];

  useEffect(() => {
    setDiscountItemIndex(0);
  }, [activeTableId]);

  useEffect(() => {
    if (!activeTable?.items.length) {
      setDiscountItemIndex(0);
      return;
    }
    setDiscountItemIndex(index => Math.min(index, activeTable.items.length - 1));
  }, [activeTable?.items.length]);

  useEffect(() => {
    const checkOverflow = () => {
      const el = categoriesContainerRef.current;
      if (el) {
        const hasOverflow = el.scrollWidth > el.clientWidth;
        if (showScrollButtons !== hasOverflow) {
          setShowScrollButtons(hasOverflow);
        }
      }
    };
    
    const timer = setTimeout(checkOverflow, 150);
    window.addEventListener('resize', checkOverflow);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [categories, showScrollButtons]);

  const handleScroll = (direction: 'left' | 'right') => {
    if (categoriesContainerRef.current) {
      const scrollAmount = categoriesContainerRef.current.clientWidth * 0.7;
      categoriesContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (tables.length > 0) setTempTableCount(tables.length);
  }, [tables]);

  useEffect(() => {
    setTempPayments([]);
    setSelectedMethod(null);
    setPaymentMode(null);
    setGeneralDiscountInput('');
    if(location.state?.tableId && activeTableId !== location.state.tableId) {
        setActiveTableId(location.state.tableId);
    }
  }, [activeTableId, location.state]);

  useEffect(() => {
    setGeneralDiscountInput(generalDiscount > 0 ? generalDiscount.toFixed(2).replace('.', ',') : '');
  }, [activeTableId, generalDiscount]);

  useEffect(() => {
    if(isPaying && selectedMethod) {
      setPaymentValue(amountDue > 0 ? amountDue.toFixed(2).replace('.', ',') : '0,00');
    }
  }, [selectedMethod, amountDue, isPaying]);
  
  const handleSelectTable = (id: number) => {
    if (location.state?.tableId) navigate('/pos', { replace: true });
    setActiveTableId(id);
  };
  
  const handleBackToTables = () => {
    setActiveTableId(currentCompany?.useCounterMode || isTradeMode ? COUNTER_TABLE_ID : null);
    navigate('/pos', { replace: true });
  }

  const getTableTitle = (table: Table) => {
    if (isTradeMode) return tradeCopy?.saleTitle || 'Venda em andamento';
    return table.name?.trim() ? `${table.label} - ${table.name.trim()}` : table.label;
  };
  const getTableStatusLabel = (table: Table) => {
    if (isTradeMode) return tradeCopy?.saleStatus || 'Venda direta';
    if (table.id === COUNTER_TABLE_ID) return 'Atendimento no balcão';
    return table.status === 'FREE' ? 'Livre' : table.status === 'OCCUPIED' ? 'Ocupada' : 'Pagando';
  };

  const handleEditActiveTableName = () => {
    if (!activeTable) return;
    setTableNameModal({ tableId: activeTable.id, value: activeTable.name || '' });
  };

  const handleSaveTableName = async () => {
    if (!tableNameModal) return;

    const { tableId, value } = tableNameModal;
    setTableNameModal(null);

    try {
      await updateTableName(tableId, value);
    } catch (error) {
      console.error('Erro ao salvar nome da mesa:', error);
      alert('Não foi possível salvar o nome da mesa. Tente novamente.');
    }
  };

  const clearPaymentDraft = () => {
    setTempPayments([]);
    setSelectedMethod(null);
    setPaymentValue('');
  };

  const parseCurrencyInput = (value: string) => normalizeMoney(value.replace(/\./g, '').replace(',', '.'));

  const handleApplyGeneralDiscount = () => {
    if (!activeTable) return;
    setAuthAction({ action: 'general_discount', discount: parseCurrencyInput(generalDiscountInput) });
  };

  const handleSetItemFinalPrice = (item: Table['items'][number]) => {
    if (!activeTable) return;
    const currentFinalPrice = getItemUnitPrice(item);
    const answer = window.prompt(
      `Preço final de "${item.productName}"\nPreço normal: ${formatCurrency(item.price)}`,
      currentFinalPrice.toFixed(2).replace('.', ',')
    );
    if (answer === null) return;
    const finalPrice = Math.min(parseCurrencyInput(answer), Number(item.price || 0));
    setAuthAction({ action: 'item_discount', itemId: item.id, discount: Math.max(0, Number(item.price || 0) - finalPrice) });
  };

  const fillPaymentWithSplitValue = () => {
    const perPerson = finalTotal / Math.max(1, splitPeople);
    setPaymentValue(perPerson.toFixed(2).replace('.', ','));
  };

  const handleRequestBill = (mode: 'advance' | 'close') => {
    if (activeTableId !== null) {
      setPaymentMode(mode);
      requestBill(activeTableId);
    }
  };

  const handleProductClick = useCallback((product: Product) => {
    if (activeTableId === null) return;

    if ((product.modifierGroups || []).length > 0) {
      setCustomizingProduct(product);
      return;
    }

    if (currentCompany?.useScale && product.saleUnit === 'KG') {
      setScaleProduct(product);
      setScaleWeight('');
      return;
    }

    addOrderToTable(activeTableId, product, 1, '');
  }, [activeTableId, currentCompany?.useScale, addOrderToTable]);

  const confirmCustomization = (price: number, notes: string) => {
    if (!customizingProduct || activeTableId === null) return;
    addOrderToTable(activeTableId, { ...customizingProduct, price }, 1, notes);
    setCustomizingProduct(null);
  };

  const normalizeLookup = (value: unknown) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, '');

  const findProductByBarcode = useCallback((rawCode: string) => {
    const code = normalizeLookup(rawCode);
    if (!code) return null;

    return products.find(product => {
      if (!isSellableProduct(product)) return false;
      const lookupProduct = product as Product & Record<string, unknown>;
      const possibleCodes = [
        lookupProduct.barcode,
        lookupProduct.barCode,
        lookupProduct.code,
        lookupProduct.sku,
        lookupProduct.ean,
        lookupProduct.gtin,
        lookupProduct.id
      ];

      return possibleCodes.some(value => normalizeLookup(value) === code) || normalizeLookup(product.name) === code;
    }) || null;
  }, [products]);

  const handleAddWeightedProduct = () => {
    if (!scaleProduct || activeTableId === null) return;

    const weight = Number(scaleWeight.replace(',', '.'));
    if (!Number.isFinite(weight) || weight <= 0) {
      alert('Informe um peso válido.');
      return;
    }

    addOrderToTable(activeTableId, scaleProduct, weight, `${weight.toString().replace('.', ',')} kg`);
    setScaleProduct(null);
    setScaleWeight('');
  };

  useEffect(() => {
    if (!isTradeMode) return;

    const resetScannerBuffer = () => {
      scannerBufferRef.current = '';
      if (scannerResetTimerRef.current) {
        window.clearTimeout(scannerResetTimerRef.current);
        scannerResetTimerRef.current = null;
      }
    };

    const isEditableTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      const tagName = target.tagName.toLowerCase();
      return target.isContentEditable || tagName === 'input' || tagName === 'textarea' || tagName === 'select';
    };

    const handleScannerKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.altKey || event.metaKey || isEditableTarget(event.target)) return;
      if (activeTableId === null || isPaying || paymentMode || authAction || scaleProduct || isTransferring || tableNameModal || pendingMergeTarget) return;

      if (event.key === 'Enter') {
        const scannedCode = scannerBufferRef.current.trim();
        resetScannerBuffer();

        if (scannedCode.length < 3) return;

        const product = findProductByBarcode(scannedCode);
        if (!product) {
          alert(`Produto não encontrado para o código ${scannedCode}.`);
          return;
        }

        event.preventDefault();
        handleProductClick(product);
        setSearchTerm('');
        return;
      }

      if (event.key.length === 1 && /^[a-zA-Z0-9._-]$/.test(event.key)) {
        scannerBufferRef.current += event.key;
        if (scannerResetTimerRef.current) window.clearTimeout(scannerResetTimerRef.current);
        scannerResetTimerRef.current = window.setTimeout(resetScannerBuffer, 180);
      }
    };

    window.addEventListener('keydown', handleScannerKeyDown);

    return () => {
      window.removeEventListener('keydown', handleScannerKeyDown);
      resetScannerBuffer();
    };
  }, [isTradeMode, activeTableId, isPaying, paymentMode, authAction, scaleProduct, isTransferring, tableNameModal, pendingMergeTarget, products, findProductByBarcode, handleProductClick]);
  
  const handleBackToOrder = () => {
    if (activeTableId !== null) {
      if (tempPayments.length > 0) {
        addPartialPayments(activeTableId, tempPayments);
        clearPaymentDraft();
      } else {
        revertToOccupied(activeTableId);
      }
      setPaymentMode(null);
    }
  };

  const handleCancelPaymentDraft = () => {
    if (activeTableId !== null) {
      clearPaymentDraft();
      revertToOccupied(activeTableId);
      setPaymentMode(null);
    }
  };
  
  const handlePrintPreview = () => {
    if (!activeTable) return;

    const previewInfo = {
      table: activeTable,
      payments: activeTable.payments || [],
      total: finalTotal,
      grossSubTotal,
      subTotal: subTotal,
      itemDiscountTotal,
      generalDiscount,
      serviceFee: serviceFeeValue,
      totalPaid: previouslyPaid,
      change: 0,
      companyName: currentCompany?.name,
      isPreview: true,
    };

    setReceiptData(previewInfo);
    setTimeout(() => { window.print(); setReceiptData(null); }, 200);
  };

  const handleAddPayment = () => {
    if (!selectedMethod || !paymentValue) return;
    const amount = parseFloat(paymentValue.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) return;
    const remaining = Math.max(0, finalTotal - previouslyPaid - tempPayments.reduce((sum, payment) => sum + payment.amount, 0));
    if (selectedMethod !== 'Dinheiro' && amount > remaining + 0.005) {
      alert('Pagamento maior que o saldo. Para informar troco, use Dinheiro.');
      return;
    }
    if (paymentMode === 'advance' && amount > remaining + 0.005) {
      alert('O adiantamento não pode ultrapassar o saldo da conta.');
      return;
    }
    setTempPayments(prev => [...prev, { id: crypto.randomUUID(), method: selectedMethod, amount: amount, timestamp: new Date() }]);
    setSelectedMethod(null); setPaymentValue('');
  };

  const handleFinalizeSale = async () => {
    if (activeTableId === null || !activeTable) return;
    
    setIsFinishing(true);
    try {
      const result = await closeTable(activeTableId, tempPayments);
      const receiptInfo = {
        table: activeTable,
        payments: result.order.payments,
        total: result.order.total,
        grossSubTotal,
        subTotal,
        itemDiscountTotal,
        generalDiscount,
        serviceFee: serviceFeeValue,
        totalPaid: result.order.payments.reduce((sum, payment) => sum + payment.amount, 0),
        change: result.change,
        companyName: currentCompany?.name,
        isPreview: false,
      };
      setReceiptData(receiptInfo);
      window.setTimeout(() => {
        window.print();
        clearPaymentDraft();
        setSplitPeople(1);
        setPaymentMode(null);
        setReceiptData(null);
        handleBackToTables();
      }, 200);
    } catch (error: any) {
      alert(error?.message || 'Não foi possível finalizar a venda. Confira a conexão e tente novamente.');
    } finally {
      setIsFinishing(false);
    }
  };

  const finishTransferTable = async (targetIdNum: number, shouldMerge: boolean) => {
    if (activeTableId === null) return;

    const result = await transferTable(activeTableId, targetIdNum, shouldMerge);
    if (result.success) {
        alert(result.message);
        setIsTransferring(false);
        setPendingMergeTarget(null);
        setTransferTargetId('');
        setActiveTableId(targetIdNum);
    } else {
        alert(`Erro: ${result.message}`);
    }
  };

  const handleTransferTable = async () => {
    const targetIdNum = parseInt(transferTargetId, 10);
    if (activeTableId === null || isNaN(targetIdNum) || targetIdNum <= 0) {
        alert('Por favor, insira um número de mesa válido.');
        return;
    }

    const destinationTable = tables.find(t => t.id === targetIdNum);
    if (!destinationTable) {
        alert('Mesa de destino não encontrada.');
        return;
    }

    if (destinationTable.items.length > 0) {
        setPendingMergeTarget(targetIdNum);
        return;
    }

    await finishTransferTable(targetIdNum, false);
  };
  const handleAuthAndModify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!authAction || !activeTable) return;
    setIsVerifying(true);
    const authResult = await verifyManagerAuth(authPassword);
    
    if (authResult) {
      let logMessage = '';
      if (authAction.action === 'reset_payment') logMessage = `Reiniciou pagamentos da ${activeTable.label}.`;
      else if (authAction.action === 'cancel_table') logMessage = `Cancelou a conta da ${activeTable.label}.`;
      else if (authAction.action === 'general_discount') logMessage = `Aplicou desconto geral de ${formatCurrency(authAction.discount || 0)} na ${activeTable.label}.`;
      else {
          const item = activeTable.items.find(i => i.id === authAction.itemId);
          if (authAction.action === 'decrease') logMessage = `Diminuiu ${decreaseAmount}x '${item?.productName}' da ${activeTable.label}.`;
          else if (authAction.action === 'item_discount') logMessage = `Aplicou preço especial em '${item?.productName}' na ${activeTable.label}.`;
          else logMessage = `Excluiu item '${item?.productName}' da ${activeTable.label}.`;
      }
      await logManagerAction(logMessage, authResult.user.name);
      
      if (authAction.action === 'decrease' && authAction.itemId) updateOrderItemQuantity(activeTable.id, authAction.itemId, -decreaseAmount);
      else if (authAction.action === 'delete' && authAction.itemId) removeItemFromTable(activeTable.id, authAction.itemId);
      else if (authAction.action === 'item_discount' && authAction.itemId) updateOrderItemDiscount(activeTable.id, authAction.itemId, authAction.discount || 0);
      else if (authAction.action === 'general_discount') setTableDiscount(activeTable.id, authAction.discount || 0);
      else if (authAction.action === 'reset_payment') {
          resetTablePayments(activeTable.id);
          setTempPayments([]);
          setSelectedMethod(null);
          setPaymentValue('');
      }
      else if (authAction.action === 'cancel_table') {
          const result = await cancelTable(activeTable.id);
          if (result.lossItemCount > 0) {
            alert(`${result.lossItemCount} item(ns) já em preparo foram registrados como perda e não voltaram ao estoque.`);
          }
          handleBackToTables();
      }

      setAuthAction(null); setAuthPassword(''); setDecreaseAmount(1);
    } else { alert('Senha de gerente inválida.'); setAuthPassword(''); }
    
    setIsVerifying(false);
  };
  
  const filteredProducts = useMemo(() => {
      const all = products.filter(p => isSellableProduct(p) && p.name.toLowerCase().includes(searchTerm.toLowerCase()));
      if (categoryFilter === 'Todos') return all;
      return all.filter(p => p.category === categoryFilter);
  }, [products, searchTerm, categoryFilter]);

  const allCategories = useMemo(() => {
      const visibleCategories = categories.filter(category => products.some(product => isSellableProduct(product) && product.category === category));
      return ['Todos', ...visibleCategories, ...(showTourExample ? ['Exemplo'] : [])];
  }, [categories, products, showTourExample]);
  const allPaymentsOnBill = [...(activeTable?.payments || []), ...tempPayments];

  const paymentMethods = [{ label: 'Dinheiro', method: 'Dinheiro', icon: Wallet }, { label: 'Pix', method: 'Pix', icon: QrCode }, { label: 'Crédito', method: 'Cartão Crédito', icon: CreditCard }, { label: 'Débito', method: 'Cartão Débito', icon: CreditCard }] as const;
  
  const isTableSelectionVisible = isTradeMode || activeTableId === null || (currentCompany?.useCounterMode && activeTableId === COUNTER_TABLE_ID);

  if (currentCompany?.useShiftManagement && cashierShift?.status !== 'OPEN') {
      return <OpenCashierScreen />;
  }
  
  return (
    <>
    {pendingProtectedGuide && !tourOpen && !tourDismissed && (
      <div className="fixed bottom-4 left-1/2 z-40 flex w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-white p-4 shadow-2xl print:hidden">
        <div className="min-w-0"><p className="text-sm font-black text-slate-900">Guia de {pendingProtectedGuide.label} aguardando</p><p className="text-xs text-slate-600">Entre na área e confirme a senha para abrir o tutorial. Você pode continuar trabalhando no PDV.</p></div>
        <div className="flex shrink-0 items-center gap-2"><button type="button" onClick={() => { if (user) stopFirstAccessTour(user.id); setTourDismissed(true); }} className="rounded-xl px-3 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100">Encerrar tour</button><button type="button" onClick={() => navigate(pendingProtectedGuide.route)} className="rounded-xl bg-slate-950 px-4 py-2 text-xs font-black text-white hover:bg-slate-800">Continuar guia</button></div>
      </div>
    )}
    <div className="flex-1 min-h-0 overflow-hidden bg-white print:hidden">
    <div
      className="h-full"
      style={desktopScale < 0.999 ? {
        transform: `scale(${desktopScale})`,
        transformOrigin: 'top left',
        width: `${100 / desktopScale}%`,
        height: `${100 / desktopScale}%`
      } : undefined}
    >
    <div className={`h-full min-h-0 flex flex-col lg:flex-row font-sans overflow-hidden ${isTradeMode ? 'bg-slate-100 p-3 gap-3' : 'bg-white'}`}>
      
      {/* Tables Column */}
      <div className={`${isTradeMode ? 'w-full lg:w-[340px] lg:rounded-[2rem] bg-slate-950 text-white border border-white/10 shadow-xl overflow-hidden' : 'w-full lg:w-[380px] border-r border-gray-100'} lg:shrink-0 min-h-0 flex-col ${!isTableSelectionVisible ? 'hidden lg:flex' : 'flex'}`}>
        {isTradeMode && tradeCopy ? (
          <>
            <div data-tour="pos-tables" className="relative p-5 border-b border-white/10 bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950">
              <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-emerald-400/20 blur-2xl" />
              <div className="relative">
                <button type="button" onClick={startTour} aria-label="Abrir guia do PDV" className="absolute right-0 top-0 rounded-xl border border-white/15 bg-white/10 p-2 text-emerald-100 hover:bg-white/20"><CircleHelp className="h-4 w-4" /></button>
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-300/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-100">
                  <ShieldCheck className="w-4 h-4" /> {tradeCopy.eyebrow}
                </span>
                <h2 className="mt-5 text-3xl font-black tracking-tight">{tradeCopy.title}</h2>
                <p className="mt-2 text-sm font-semibold text-slate-300 leading-relaxed">{tradeCopy.subtitle}</p>
                {currentCompany?.useShiftManagement && (
                  <span className="mt-4 inline-flex text-xs font-black text-emerald-200 bg-emerald-400/10 border border-emerald-300/20 px-3 py-1 rounded-full">CAIXA ABERTO</span>
                )}
              </div>
            </div>
            <div className="p-4 grid gap-3 overflow-y-auto no-scrollbar">
              <div className="rounded-3xl border border-white/10 bg-white/8 p-4">
                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-300">Operação atual</p>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xl font-black">{tradeCopy.saleTitle}</p>
                    <p className="text-xs font-bold text-slate-400">{tradeCopy.saleStatus}</p>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-emerald-300 text-slate-950 flex items-center justify-center">
                    {posMode === 'MARKET' ? <ScanBarcode className="w-6 h-6" /> : <Package className="w-6 h-6" />}
                  </div>
                </div>
              </div>

              {tradeCopy.guidance.map(({ title, text, icon: Icon }) => (
                <div key={title} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-white/10 text-emerald-200 border border-white/10 flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                    <div>
                      <p className="font-black text-white">{title}</p>
                      <p className="mt-1 text-xs font-semibold text-slate-400">{text}</p>
                    </div>
                  </div>
                </div>
              ))}
              <div className="rounded-2xl border border-emerald-300/20 bg-emerald-300/10 p-4">
                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-200">Resumo do caixa</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-white/10 p-3"><p className="text-[10px] font-bold text-slate-400">Itens</p><p className="text-2xl font-black">{activeTable?.items.length || 0}</p></div>
                  <div className="rounded-xl bg-white/10 p-3"><p className="text-[10px] font-bold text-slate-400">Total</p><p className="text-lg font-black">{formatCurrency(subTotal)}</p></div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
        <div data-tour={tables.length === 0 ? 'pos-tables' : undefined} className="p-4 border-b border-gray-100 flex justify-between items-center">
            <div>
                <h2 className="font-bold text-gray-800 text-lg">Mesas</h2>
                {currentCompany?.useShiftManagement && (
                    <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">CAIXA ABERTO</span>
                )}
            </div>
            <div className="flex items-center gap-1">
                 <button type="button" onClick={startTour} aria-label="Abrir guia do PDV" className="p-2 text-gray-400 hover:text-brand-600 rounded-lg" title="Guia do PDV"><CircleHelp className="w-5 h-5" /></button>
                 <button onClick={() => { setTempTableCount(tables.length); setShowConfig(true); }} className="p-2 text-gray-400 hover:text-brand-600 rounded-lg" title="Configurar Mesas"><Settings className="w-5 h-5" /></button>
            </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-3 sm:grid-cols-4 gap-3 content-start no-scrollbar">
          {tables.filter(t => !(currentCompany?.useCounterMode && t.id === COUNTER_TABLE_ID)).map((table, index) => {
            const statusStyles = { FREE: 'bg-white border-gray-200 text-gray-500', OCCUPIED: 'bg-blue-50 border-blue-400 text-blue-800', PAYING: 'bg-red-100 border-red-400 text-red-800' };
            return (
              <button key={table.id} data-tour={index === 0 ? 'pos-tables' : undefined} onClick={() => handleSelectTable(table.id)}
                className={`aspect-square rounded-2xl border-2 flex flex-col items-center justify-center transition-all duration-200 shadow-sm active:scale-95 ${statusStyles[table.status]}`}>
                <span className="font-black text-3xl text-gray-700 leading-none">{table.id}</span>
                {table.name?.trim() && <span className="max-w-full px-1 text-[10px] font-black text-gray-700 truncate">{table.name}</span>}
                <span className={`text-[9px] font-bold uppercase tracking-wider`}>{table.status === 'FREE' ? 'Livre' : table.status === 'OCCUPIED' ? 'Ocupada' : 'Pagando'}</span>
              </button>
            )
          })}
        </div>
          </>
        )}
      </div>

      {/* Order & Product Details Column */}
      <div className={`flex-1 min-w-0 min-h-0 flex flex-col lg:flex-row ${isTradeMode ? 'bg-transparent gap-3' : 'bg-gray-50'} ${!activeTable ? 'hidden lg:flex' : 'flex'}`}>
        {/* Order Details */}
        <div className={`flex-1 min-w-0 min-h-0 flex flex-col bg-white h-[52dvh] lg:h-auto ${isTradeMode ? 'rounded-[2rem] border border-slate-200 shadow-xl overflow-hidden' : ''}`}>
          {!activeTable ? (
            <div data-tour="pos-order" className="flex-1 flex flex-col items-center justify-center text-gray-300">
              {isTradeMode ? <ShoppingCart className="w-16 h-16 mb-4" /> : <UtensilsCrossed className="w-16 h-16 mb-4" />}
              <p className="font-semibold text-lg">{isTradeMode ? 'Venda pronta para iniciar' : 'Nenhuma mesa selecionada'}</p>
            </div>
          ) : (
            <>
              <div data-tour="pos-order" className={`shrink-0 p-4 border-b flex justify-between items-center ${isTradeMode ? 'border-slate-200 bg-gradient-to-r from-white to-slate-50' : 'border-gray-200 bg-white'}`}>
                <div className='flex items-center gap-2'>
                  <button onClick={handleBackToTables} className="p-2 -ml-2 text-gray-500 lg:hidden"><ArrowLeft/></button>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className={`${isTradeMode ? 'font-black text-2xl' : 'font-bold text-xl'} text-gray-900`}>{getTableTitle(activeTable)}</h2>
                      {!isTradeMode && activeTable.id !== COUNTER_TABLE_ID && (
                        <button onClick={handleEditActiveTableName} className="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50" title="Editar nome da mesa">
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <p className={`text-xs font-bold uppercase tracking-wider ${isPaying ? 'text-red-500' : 'text-gray-500'}`}>{getTableStatusLabel(activeTable)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 font-medium">Subtotal</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(subTotal)}</p>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-2 sm:p-4 space-y-2 no-scrollbar">
                {activeTable.items.length === 0 && showTourExample && tourStep >= 4 ? (
                  <div className="rounded-2xl border border-dashed border-emerald-300 bg-emerald-50 p-4 text-sm text-slate-700">
                    <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Prévia do guia · não salva</p>
                    <div className="mt-2 flex justify-between gap-3 font-bold"><span>1× Produto de exemplo</span><span>R$ 12,00</span></div>
                  </div>
                ) : activeTable.items.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center text-gray-400">
                    {isTradeMode ? <ShoppingCart className="w-14 h-14 mb-3 text-slate-300" /> : null}
                    <p>{isTradeMode ? tradeCopy?.empty : 'Sem pedidos'}</p>
                  </div>
                ) : activeTable.items.map(item => {
                  const itemDiscount = getItemDiscount(item);
                  const itemUnitPrice = getItemUnitPrice(item);
                  const itemTotal = calculateOrderItemTotal(item);
                  return (
                  <div key={item.id} className={`p-3 bg-white rounded-xl shadow-sm flex items-center border ${isTradeMode ? 'border-slate-200 shadow-md' : 'border-gray-100'}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-800 text-sm truncate">{item.productName}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        {itemDiscount > 0 ? (
                          <>
                            <span className="text-gray-400 line-through">{formatCurrency(item.price)}</span>
                            <span className="font-black text-emerald-600">{formatCurrency(itemUnitPrice)}</span>
                          </>
                        ) : (
                          <span className="text-gray-500">{formatCurrency(item.price)}</span>
                        )}
                        {itemDiscount > 0 && <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-black text-emerald-700">Preço especial</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-3 ml-2 sm:ml-4">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <button onClick={() => updateOrderItemQuantity(activeTable.id, item.id, 1)} disabled={isPaying} className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg text-gray-700 disabled:opacity-50 hover:bg-gray-200 transition-colors">+</button>
                        <span className="font-bold w-6 text-center">{item.quantity}</span>
                        <button onClick={() => setAuthAction({action: 'decrease', itemId: item.id})} disabled={isPaying} className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg text-gray-700 disabled:opacity-50 hover:bg-gray-200 transition-colors">-</button>
                      </div>
                      <button onClick={() => setAuthAction({action: 'delete', itemId: item.id})} disabled={isPaying} className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      <p className="font-bold w-16 sm:w-20 text-right text-sm">{formatCurrency(itemTotal)}</p>
                    </div>
                  </div>
                  );
                })}
              </div>
              <div data-tour="pos-actions" className="shrink-0 p-4 border-t border-gray-200 bg-white space-y-2">
                {!isTradeMode && (
                <div className="grid grid-cols-3 gap-2">
                    <Button onClick={handlePrintPreview} disabled={activeTable.items.length === 0} variant="secondary" className="h-16 rounded-xl flex-col text-[10px] leading-tight pt-1">
                        <Printer className="w-5 h-5 mb-1"/>
                        Imprimir
                    </Button>
                    {!isTradeMode && (
                      <Button onClick={() => setIsTransferring(true)} disabled={activeTable.items.length === 0 || isPaying || activeTableId === COUNTER_TABLE_ID} variant="secondary" className="h-16 rounded-xl flex-col text-[10px] leading-tight pt-1">
                          <ArrowRightLeft className="w-5 h-5 mb-1 text-orange-500"/>
                          Mover Mesa
                      </Button>
                    )}
                    <Button onClick={handleBackToTables} variant="success" className="h-16 rounded-xl flex-col text-[10px] leading-tight pt-1">
                        <CheckCircle className="w-5 h-5 mb-1"/>
                        {isTradeMode ? 'Salvar Venda' : 'Concluir'}
                    </Button>
                </div>
                )}
                <div className={`grid ${isTradeMode ? 'grid-cols-2' : 'grid-cols-3'} gap-2`}>
                    <Button onClick={() => setAuthAction({action: 'cancel_table'})} disabled={activeTable.items.length === 0 || isPaying} variant="secondary" className="h-16 rounded-xl flex-col text-[10px] leading-tight pt-1 !text-red-500 hover:!bg-red-50 hover:!border-red-200">
                        <Ban className="w-5 h-5 mb-1"/>
                        Cancelar
                    </Button>
                    {!isTradeMode && (
                    <Button onClick={() => handleRequestBill('advance')} disabled={activeTable.items.length === 0 || isPaying} variant="primary" className="h-16 rounded-xl flex-col text-[10px] leading-tight pt-1">
                        <Clock className="w-5 h-5 mb-1"/>
                        Adiantamento
                    </Button>
                    )}
                    <Button data-tour="pos-charge" onClick={() => handleRequestBill('close')} disabled={activeTable.items.length === 0 || isPaying} variant="danger" className="h-16 rounded-xl flex-col text-[10px] leading-tight pt-1">
                        <Wallet className="w-5 h-5 mb-1"/>
                        {tradeCopy?.closeLabel || 'Fechar Conta'}
                    </Button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Product Grid or Payment Panel */}
        <div className={`w-full ${isTradeMode ? 'lg:w-[520px] rounded-[2rem] border border-slate-200 shadow-xl overflow-hidden' : 'lg:w-[480px] border-t lg:border-t-0 lg:border-l border-gray-100'} lg:shrink-0 min-h-0 bg-white flex flex-col h-[48dvh] lg:h-auto`}>
          {tourOpen && tourStep === 7 ? (
            <div className="flex h-full flex-col bg-white p-5">
              <span className="w-fit rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">Prévia do tutorial · sem cobrança</span>
              <h2 className="mt-5 text-xl font-black text-slate-950">Pagamento da comanda</h2>
              <p className="mt-2 text-sm text-slate-500">Após fechar a conta, escolha como o cliente pagou.</p>
              <div data-tour="pos-payment" className="mt-5 grid grid-cols-2 gap-3">
                {paymentMethods.map(({ label, icon: Icon }) => <div key={label} className="flex h-20 flex-col items-center justify-center gap-1 rounded-2xl border border-slate-200 bg-slate-50 text-xs font-bold text-slate-700"><Icon className="h-5 w-5 text-emerald-600" />{label}</div>)}
              </div>
              <p className="mt-5 rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-xs font-semibold text-emerald-800">Exemplo apenas visual. Nenhum pagamento ou venda será registrado.</p>
            </div>
          ) : isPaying && activeTable ? (
             <div className="flex flex-col h-full bg-white">
                <header className="bg-red-600 text-white p-4 flex items-center justify-between shadow-md">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-6 h-6"/>
                      <div>
                        <h2 className="font-bold text-lg leading-tight">
                          {paymentMode === 'advance' ? 'Adiantamento' : 'Pagamento'}
                        </h2>
                        {paymentMode === 'advance' && <p className="text-xs font-semibold text-red-100">Registre um valor parcial e salve a comanda.</p>}
                      </div>
                    </div>
                    <p className="font-black text-2xl tracking-tight">{formatCurrency(finalTotal)}</p>
                </header>
                <div className="p-4 text-sm divide-y divide-gray-100 border-b">
                    <div className="flex justify-between items-center py-2"><span className="text-gray-600">Subtotal</span><span className="font-semibold">{formatCurrency(subTotal)}</span></div>
                    {itemDiscountTotal > 0 && <div className="flex justify-between items-center py-2 text-emerald-600"><span>Desconto nos itens</span><span className="font-semibold">-{formatCurrency(itemDiscountTotal)}</span></div>}
                    <div className="py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-gray-600">Desconto geral</span>
                        <div className="flex items-center gap-2">
                          <input value={generalDiscountInput} onChange={event => setGeneralDiscountInput(event.target.value)} placeholder="0,00" className="w-24 rounded-lg border border-gray-200 px-2 py-1 text-right font-bold outline-none focus:border-brand-500" />
                          <button type="button" onClick={handleApplyGeneralDiscount} className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-black text-white">Aplicar</button>
                        </div>
                      </div>
                      {generalDiscount > 0 && <p className="mt-1 text-right text-xs font-bold text-emerald-600">-{formatCurrency(generalDiscount)}</p>}
                    </div>
                    {discountItem && (
                      <div className="py-2">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <p className="text-xs font-black uppercase tracking-widest text-gray-400">Preço especial por item</p>
                          <span className="text-[11px] font-black text-gray-400">{discountItemIndex + 1}/{activeTable.items.length}</span>
                        </div>
                        <div className="flex items-center gap-2 rounded-xl bg-gray-50 px-2 py-2">
                          <button type="button" onClick={() => setDiscountItemIndex(index => Math.max(0, index - 1))} disabled={discountItemIndex === 0} className="h-8 w-8 shrink-0 rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-30"><ChevronLeft className="mx-auto h-4 w-4" /></button>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-black text-gray-800">{discountItem.quantity}x {discountItem.productName}</p>
                            <p className="text-[11px] font-bold text-gray-500">
                              Normal {formatCurrency(discountItem.price)}
                              {getItemDiscount(discountItem) > 0 && <span className="ml-2 text-emerald-600">Final {formatCurrency(getItemUnitPrice(discountItem))}</span>}
                            </p>
                          </div>
                          <button type="button" onClick={() => handleSetItemFinalPrice(discountItem)} className="shrink-0 rounded-lg border border-emerald-100 bg-white px-3 py-1.5 text-[11px] font-black text-emerald-700">
                            {getItemDiscount(discountItem) > 0 ? 'Editar' : 'Aplicar'}
                          </button>
                          <button type="button" onClick={() => setDiscountItemIndex(index => Math.min(activeTable.items.length - 1, index + 1))} disabled={discountItemIndex >= activeTable.items.length - 1} className="h-8 w-8 shrink-0 rounded-lg bg-white border border-gray-200 text-gray-700 disabled:opacity-30"><ChevronRight className="mx-auto h-4 w-4" /></button>
                        </div>
                      </div>
                    )}
                    {isServiceFeeEnabled && <div className="flex justify-between items-center py-2"><label className="flex items-center gap-2 text-gray-600 cursor-pointer"><input type="checkbox" checked={activeTable.serviceFee} onChange={() => toggleServiceFee(activeTable.id)} className="form-checkbox h-4 w-4 text-brand-600 rounded" /> 10% Serviço</label><span className="font-semibold">{formatCurrency(serviceFeeValue)}</span></div>}
                    <div className="flex justify-between items-center py-2 text-gray-600"><span >Pago</span><span className="font-semibold text-green-600">{formatCurrency(totalPaid)}</span></div>
                    <div className="flex justify-between items-center font-black text-2xl pt-3 text-red-600"><span>Pendente</span><span>{formatCurrency(amountDue)}</span></div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {amountDue > 0.005 ? (
                    <>
                      <div data-tour="pos-payment" className="grid grid-cols-2 gap-3">
                        {paymentMethods.map(({label, method, icon: Icon}) => ( <button key={method} onClick={() => setSelectedMethod(method)} className={`p-4 border-2 rounded-2xl flex flex-col items-center justify-center gap-2 h-24 transition-all ${selectedMethod === method ? 'border-brand-500 bg-brand-50 shadow-lg' : 'border-gray-200 bg-white hover:bg-gray-50'}`}><Icon className={`w-6 h-6 ${selectedMethod === method ? 'text-brand-600' : 'text-gray-400'}`} /> <span className={`font-bold text-sm ${selectedMethod === method ? 'text-brand-700' : 'text-gray-700'}`}>{label}</span></button>))}
                      </div>
                      {selectedMethod && (
                        <div className="space-y-2 pt-2">
                          <div className="flex items-center gap-2">
                            <input type="text" value={paymentValue} onChange={e => setPaymentValue(e.target.value)} className="flex-1 p-3 border-2 border-gray-200 rounded-xl font-bold text-lg outline-none focus:border-brand-500 text-center" />
                            <Button onClick={handleAddPayment} className="h-14 w-14 rounded-xl"><Check className="w-6 h-6"/></Button>
                          </div>
                          <button type="button" onClick={fillPaymentWithSplitValue} className="w-full rounded-xl bg-brand-50 border border-brand-100 py-2 text-xs font-black text-brand-700">
                            Usar valor por pessoa: {formatCurrency(finalTotal / Math.max(1, splitPeople))}
                          </button>
                        </div>
                      )}
                    </>
                  ) : <div className="p-8 text-center bg-green-50 flex-1 flex flex-col justify-center items-center rounded-xl"><Check className="w-12 h-12 text-green-500 mb-2" /><h3 className="font-bold text-lg text-green-800">{isTradeMode ? 'Venda paga' : 'Conta Paga'}</h3></div>}
                  {(
                    <div className="rounded-2xl border border-brand-100 bg-brand-50 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-black uppercase tracking-widest text-brand-500">Dividir conta</p>
                          <p className="text-sm font-bold text-brand-900">{formatCurrency(finalTotal / Math.max(1, splitPeople))} por pessoa</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => setSplitPeople(value => Math.max(1, value - 1))} className="w-8 h-8 rounded-lg bg-white border border-brand-100 font-black text-brand-700">-</button>
                          <span className="w-8 text-center font-black text-brand-900">{splitPeople}</span>
                          <button onClick={() => setSplitPeople(value => value + 1)} className="w-8 h-8 rounded-lg bg-white border border-brand-100 font-black text-brand-700">+</button>
                        </div>
                      </div>
                    </div>
                  )}
                   <div className="space-y-2 pt-2">{allPaymentsOnBill.map(p => (<div key={p.id} className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded-lg border border-gray-200"><span>{p.method}</span><span className="font-semibold">{formatCurrency(p.amount)}</span></div>))}</div>
                   {allPaymentsOnBill.length > 0 && <button onClick={() => setAuthAction({action: 'reset_payment'})} className="text-xs text-red-500 hover:underline mt-2">Reiniciar Pagamentos</button>}
                </div>
                <div className="p-4 border-t flex gap-2">
                  <Button
                    onClick={paymentMode === 'advance' ? handleCancelPaymentDraft : handleBackToOrder}
                    variant="secondary"
                    className="flex-1 h-12 rounded-xl"
                  >
                    {paymentMode === 'advance' ? 'Cancelar' : 'Voltar'}
                  </Button>
                  <Button
                    onClick={paymentMode === 'advance' ? handleBackToOrder : handleFinalizeSale}
                    disabled={paymentMode === 'advance' ? tempPayments.length === 0 : amountDue > 0.005 || isFinishing}
                    className="flex-1 h-12 rounded-xl"
                  >
                    {paymentMode === 'advance' ? 'Confirmar parcial' : isFinishing ? <RefreshIcon className="animate-spin" /> : 'Finalizar'}
                  </Button>
                </div>
             </div>
          ) : (
            <div className="flex flex-col h-full min-h-0">
              <div data-tour="pos-search" className="shrink-0 p-4 border-b border-gray-100 space-y-2">
                {isTradeMode && (
                  <div className="flex items-center gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-3 py-2 text-[11px] font-black text-emerald-800">
                    <ScanBarcode className="w-4 h-4 shrink-0" />
                    Scanner automático ativo: leia o código e o produto entra no pedido.
                  </div>
                )}
                <input type="text" placeholder={tradeCopy?.searchPlaceholder || 'Buscar produto...'} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full p-3 border rounded-2xl outline-none focus:border-brand-500 text-sm ${isTradeMode ? 'border-slate-200 bg-slate-50 font-bold' : 'border-gray-300'}`} />
              </div>
              <div data-tour="pos-categories" className="shrink-0 p-2 border-b border-gray-100 flex items-center gap-2">
                <button
                  onClick={() => handleScroll('left')}
                  aria-label="Rolar categorias para esquerda"
                  className={`p-1.5 rounded-full shadow border border-gray-200 bg-white hover:bg-gray-100 active:scale-95 transition-opacity ${showScrollButtons ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                >
                  <ChevronLeft className="w-4 h-4 text-gray-700" />
                </button>
                <div ref={categoriesContainerRef} className="flex-1 flex gap-1 overflow-x-auto no-scrollbar scroll-smooth">
                  {allCategories.map(c => <button key={c} onClick={() => setCategoryFilter(c)} disabled={c === 'Exemplo' && showTourExample} className={`px-3 py-1 rounded-full text-sm font-semibold whitespace-nowrap ${c === 'Exemplo' && showTourExample ? 'border border-dashed border-emerald-300 bg-emerald-50 text-emerald-700' : categoryFilter === c ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600'}`}>{c}</button>)}
                </div>
                <button
                  onClick={() => handleScroll('right')}
                  aria-label="Rolar categorias para direita"
                  className={`p-1.5 rounded-full shadow border border-gray-200 bg-white hover:bg-gray-100 active:scale-95 transition-opacity ${showScrollButtons ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                >
                  <ChevronRight className="w-4 h-4 text-gray-700" />
                </button>
              </div>
              <div className={`flex-1 min-h-0 overflow-y-auto p-3 grid ${isTradeMode ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-3'} gap-2 content-start no-scrollbar`}>
                 {filteredProducts.map((product, index) => {
                   const packageProduct = product.stockPackageProductId ? products.find(item => item.id === product.stockPackageProductId) : undefined;
                   const canOpenPackage = Boolean(product.stockPackageProductId && (product.stockPackageSize || 0) > 0 && (packageProduct?.stockQuantity || 0) > 0);
                   const isOutOfStock = product.trackStock && (product.stockQuantity ?? 0) <= 0 && !canOpenPackage;
                   return (
                     <button key={product.id} data-tour={index === 0 ? 'pos-products' : undefined} onClick={() => handleProductClick(product)} disabled={activeTableId === null || isPaying || isOutOfStock} className={`min-w-0 border bg-white text-left group disabled:opacity-50 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ${isTradeMode ? 'p-1.5 rounded-2xl border-slate-200' : 'p-2 rounded-xl border-gray-100'}`}>
                       <div className={`w-full aspect-square overflow-hidden relative ${isTradeMode ? 'rounded-xl mb-1.5' : 'rounded-md mb-2'}`}>
                          <img src={product.image} alt={product.name} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${isOutOfStock ? 'grayscale' : ''}`} />
                          {isOutOfStock && (
                              <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><span className="px-2 py-1 bg-red-500 text-white text-[10px] font-bold rounded">ESGOTADO</span></div>
                          )}
                       </div>
                       <p className={`${isTradeMode ? 'text-[11px]' : 'text-xs'} font-bold text-gray-800 truncate px-1`}>{product.name}</p>
                       <p className={`${isTradeMode ? 'text-[11px]' : 'text-xs'} font-semibold text-brand-600 px-1`}>{formatCurrency(product.price)}{product.saleUnit === 'KG' ? '/kg' : ''}</p>
                     </button>
                   );
                 })}
                 {showTourExample && <div data-tour="pos-products" className="rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50 p-3 text-left shadow-sm">
                   <div className="flex aspect-square items-center justify-center rounded-xl bg-white text-emerald-600"><ShoppingCart className="h-10 w-10" /></div>
                   <p className="mt-2 text-xs font-black text-slate-900">Produto de exemplo</p>
                   <p className="text-xs font-bold text-emerald-700">R$ 12,00</p>
                   <p className="mt-1 text-[10px] font-semibold text-emerald-700">Só no tutorial</p>
                 </div>}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
      </div>

      {authAction && <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"><div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 animate-in zoom-in-95"><h3 className="text-lg font-bold mb-4">Autorização Necessária</h3><form onSubmit={handleAuthAndModify} className="space-y-4">{authAction.action === 'decrease' && (<div className="flex items-center justify-center gap-4"><button type="button" onClick={() => setDecreaseAmount(v => Math.max(1, v - 1))} className="w-10 h-10 bg-gray-200 rounded">-</button><span className="text-2xl font-bold">{decreaseAmount}</span><button type="button" onClick={() => setDecreaseAmount(v => v + 1)} className="w-10 h-10 bg-gray-200 rounded">+</button></div>)}<div><label className="text-xs font-semibold">Senha do Gerente/Dono</label><input type="password" autoFocus value={authPassword} onChange={e => setAuthPassword(e.target.value)} className="w-full p-2 border border-gray-300 rounded mt-1" /></div><div className="flex gap-2"><Button type="button" variant="secondary" onClick={() => setAuthAction(null)} className="flex-1">Cancelar</Button><Button type="submit" disabled={isVerifying} className="flex-1">{isVerifying ? <RefreshIcon className="animate-spin"/> : 'Confirmar'}</Button></div></form></div></div>}
      {!isTradeMode && showConfig && <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"><div className="bg-white rounded-2xl w-full max-w-xs p-6"><h3 className="text-lg font-bold mb-4">Configurar Mesas</h3><div className="flex items-center justify-center gap-4 my-4"><button onClick={() => setTempTableCount(v => Math.max(1, v - 1))} className="w-10 h-10 bg-gray-200 rounded">-</button><span className="text-2xl font-bold">{tempTableCount}</span><button onClick={() => setTempTableCount(v => v + 1)} className="w-10 h-10 bg-gray-200 rounded">+</button></div><div className="flex gap-2"><Button variant="secondary" onClick={() => setShowConfig(false)} className="flex-1">Cancelar</Button><Button onClick={() => {updateTableCount(tempTableCount); setShowConfig(false);}} className="flex-1">Salvar</Button></div></div></div>}
      {tourOpen && <SpotlightTour label="Guia do PDV" steps={currentTourSteps} stepIndex={tourStep} onStepChange={changeTourStep} onFinish={finishTour} />}
      {scaleProduct && (
        <div className="fixed inset-0 z-[215] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 animate-in zoom-in-95">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Produto por Kg</h3>
            <p className="text-sm text-gray-500 mb-4">Informe o peso de <strong>{scaleProduct.name}</strong>. O valor será calculado pelo preço por kg.</p>
            <input
              type="text"
              value={scaleWeight}
              onChange={event => setScaleWeight(event.target.value)}
              placeholder="Ex: 0,350"
              className="w-full p-4 border-2 border-gray-200 rounded-xl text-center font-black text-2xl outline-none focus:border-brand-500"
              autoFocus
            />
            <div className="rounded-2xl bg-amber-50 border border-amber-100 p-3 mt-3 text-sm text-amber-900">
              Total: <strong>{formatCurrency((Number(scaleWeight.replace(',', '.')) || 0) * scaleProduct.price)}</strong>
            </div>
            <div className="flex gap-2 mt-5">
              <Button type="button" variant="secondary" onClick={() => setScaleProduct(null)} className="flex-1">Cancelar</Button>
              <Button type="button" onClick={handleAddWeightedProduct} className="flex-1">Adicionar</Button>
            </div>
          </div>
        </div>
      )}
      {customizingProduct && <ProductCustomizationModal product={customizingProduct} onCancel={() => setCustomizingProduct(null)} onConfirm={confirmCustomization} />}
      {!isTradeMode && tableNameModal && (
        <div className="fixed inset-0 z-[210] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 animate-in zoom-in-95">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Nome da mesa</h3>
            <p className="text-sm text-gray-500 mb-4">Use para identificar cliente, reserva ou apelido da comanda.</p>
            <input
              type="text"
              value={tableNameModal.value}
              onChange={e => setTableNameModal(prev => prev ? { ...prev, value: e.target.value } : prev)}
              placeholder="Ex: João, Reserva 20h, Família Silva"
              maxLength={40}
              className="w-full p-4 border-2 border-gray-200 rounded-xl font-bold text-lg outline-none focus:border-brand-500"
              autoFocus
            />
            <p className="text-[11px] text-gray-400 mt-2">Deixe vazio e salve para remover o nome.</p>
            <div className="flex gap-2 mt-5">
              <Button type="button" variant="secondary" onClick={() => setTableNameModal(null)} className="flex-1">Cancelar</Button>
              <Button type="button" onClick={handleSaveTableName} className="flex-1">Salvar</Button>
            </div>
          </div>
        </div>
      )}
      {!isTradeMode && pendingMergeTarget !== null && activeTable && (
        <div className="fixed inset-0 z-[220] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 animate-in zoom-in-95">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Juntar comandas?</h3>
            <p className="text-sm text-gray-500 mb-5">{getTableTitle(tables.find(t => t.id === pendingMergeTarget) || activeTable)} já tem itens. Quer juntar os itens e pagamentos da {getTableTitle(activeTable)} nela?</p>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setPendingMergeTarget(null)} className="flex-1">Cancelar</Button>
              <Button type="button" onClick={() => finishTransferTable(pendingMergeTarget, true)} className="flex-1">Juntar</Button>
            </div>
          </div>
        </div>
      )}
      {!isTradeMode && isTransferring && activeTable && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 animate-in zoom-in-95">
                <h3 className="text-lg font-bold mb-1">Mover {activeTable.label}</h3>
                <p className="text-sm text-gray-500 mb-4">Para qual mesa deseja mover todos os itens e pagamentos?</p>
                <div className="space-y-4">
                    <input 
                        type="number" 
                        placeholder="Número da mesa de destino"
                        value={transferTargetId}
                        onChange={e => setTransferTargetId(e.target.value)}
                        className="w-full p-4 border-2 border-gray-200 rounded-xl text-center font-bold text-2xl outline-none focus:border-brand-500"
                        autoFocus
                    />
                    <div className="flex gap-2">
                        <Button type="button" variant="secondary" onClick={() => setIsTransferring(false)} className="flex-1">Cancelar</Button>
                        <Button type="button" onClick={handleTransferTable} className="flex-1">Confirmar</Button>
                    </div>
                </div>
            </div>
        </div>
      )}
    </div>
    <div id="printable-receipt" className="hidden print:block relative overflow-hidden bg-white font-mono text-black text-[10px] leading-tight w-full">
      {receiptData && (
        <div className="relative p-2">
          <div className="relative z-10">
            <header className="text-center mb-3 pb-2 border-b border-dashed border-black">
              <h1 className="text-xs font-bold uppercase">{receiptData.companyName || 'FlowTab'}</h1>
              <p className="font-bold">{receiptData.isPreview ? 'CONFERÊNCIA DE CONTA' : 'COMPROVANTE DE PAGAMENTO'}</p>
              <p>{receiptData.table.label}</p>
              <p>Emitido em: {new Date().toLocaleString('pt-BR')}</p>
            </header>

            <section className="mb-2">
              <h2 className="font-bold border-b border-dashed border-black mb-1">Itens Consumidos</h2>
              {receiptData.table.items.map((item: any) => {
                const itemDiscount = getItemDiscount(item);
                return (
                <React.Fragment key={item.id}>
                  <div className="grid grid-cols-12 gap-1 text-[9px]">
                    <div className="col-span-1">{item.quantity}x</div>
                    <div className="col-span-8 truncate">{item.productName}{itemDiscount > 0 ? ` (${formatCurrency(getItemUnitPrice(item))}/un)` : ''}</div>
                    <div className="col-span-3 text-right">{formatCurrency(calculateOrderItemTotal(item))}</div>
                  </div>
                  {item.notes && <div className="ml-[8.333%] text-[8px] leading-tight">{item.notes}</div>}
                </React.Fragment>
                );
              })}
            </section>
            
            <section className="border-t border-dashed border-black pt-2 text-xs">
              <div className="flex justify-between"><span className="font-semibold">Subtotal:</span><span>{formatCurrency(receiptData.grossSubTotal ?? receiptData.subTotal)}</span></div>
              {receiptData.itemDiscountTotal > 0 && (
                <div className="flex justify-between"><span className="font-semibold">Desc. itens:</span><span>-{formatCurrency(receiptData.itemDiscountTotal)}</span></div>
              )}
              {receiptData.generalDiscount > 0 && (
                <div className="flex justify-between"><span className="font-semibold">Desc. geral:</span><span>-{formatCurrency(receiptData.generalDiscount)}</span></div>
              )}
              {receiptData.serviceFee > 0 && (
                <div className="flex justify-between"><span className="font-semibold">Serviço (10%):</span><span>{formatCurrency(receiptData.serviceFee)}</span></div>
              )}
              <div className="flex justify-between text-sm font-bold mt-1"><span>TOTAL:</span><span>{formatCurrency(receiptData.total)}</span></div>
            </section>
            
            {!receiptData.isPreview && (
              <section className="border-t border-dashed border-black pt-2 mt-2">
                <h2 className="font-bold mb-1 text-xs">Pagamento</h2>
                {receiptData.payments.map((p: any) => (
                  <div key={p.id} className="flex justify-between text-xs">
                    <span>{p.method}:</span><span>{formatCurrency(p.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-bold mt-1 text-xs"><span>Total Pago:</span><span>{formatCurrency(receiptData.totalPaid)}</span></div>
                {receiptData.change > 0.005 && (
                  <div className="flex justify-between font-bold text-xs"><span>Troco:</span><span>{formatCurrency(receiptData.change)}</span></div>
                )}
              </section>
            )}

            <footer className="mt-4 pt-3 border-t border-dashed border-black text-[8px]">
              <p className="text-center">Obrigado pela preferência!</p>
              {receiptData.isPreview && <p className="text-center">Este não é um documento fiscal.</p>}
              <img
                src="/flowtab-logo-dark-transparent.png"
                alt="FlowTab"
                className="w-[36mm] mx-auto mt-3 mb-1 opacity-85"
                style={{ transform: 'translateX(-4mm)' }}
              />
              <p className="text-center">FlowTab - controle de comandas</p>
            </footer>
          </div>
        </div>
      )}
    </div>
    </>
  );
};
